// /administracion/mantenciones — Administración · Mantenciones periódicas
// (handoff autogestión 2026-10-29 §6, plan 2026-10-30 §2.7, T-aviu).
//
// El gerente define qué se mantiene, cada cuánto y en qué tiendas, sobre
// el catálogo que hasta ahora sólo se cargaba por planilla
// (/maintenance-items, ADR-0083). Tabla + tres drawers: Asignar tiendas,
// Editar, Nueva mantención.
//
// Lo que el handoff dibuja y aquí no aparece, porque el backend aún no lo
// tiene y esta ronda no migra (se oculta, no se simula):
//   - «Proveedor sugerido» (suggested_provider_id) y «Avisar antes»
//     (notify_before_days): columna y campos.
//   - «Próxima»: la fecha es por tienda (maintenanceDueState sobre la
//     última realización de cada una) y GET /maintenance-items no trae
//     ninguna fecha; sin un dato por ítem la columna no se muestra.
//
// La guarda de la pantalla (canManageMaintenanceItems) la monta App.tsx;
// aquí sólo apaga la query para un rol que no administra, así un montaje
// sin guarda nunca dispara un 403.

import { useMemo, useState } from "react";
import type { MaintenanceItemAdminSummary, Role } from "@bitacorapro/api-types";
import { EmptyState } from "../../components/EmptyState";
import { Icon } from "../../components/icons";
import { PageHeader } from "../../components/PageHeader";
import { Panel } from "../../components/Panel";
import { QueryBoundary } from "../../components/QueryBoundary";
import { SkelTable } from "../../components/skeleton/SkelTable";
import type { StorePickerItem } from "../../components/StorePicker";
import { AsignarTiendasDrawer } from "../../features/administracion/mantenciones/AsignarTiendasDrawer";
import { EditarMantencionDrawer } from "../../features/administracion/mantenciones/EditarMantencionDrawer";
import { NuevaMantencionDrawer } from "../../features/administracion/mantenciones/NuevaMantencionDrawer";
import { frequencyLabel } from "../../features/administracion/mantenciones/cadence";
import { useMaintenanceItemsAdminList } from "../../features/administracion/mantenciones/queries";
import { appliesToLabel, mantencionWords } from "../../features/administracion/mantenciones/words";
import { adminWords } from "../../lib/admin/words";
import { useFacilityDashboard, useMyOrganization, useRecipe } from "../../lib/api/queries";
import { useSession } from "../../lib/auth/useSession";
import { canManageMaintenanceItems, isFacilityDashboardEnabled } from "../../lib/capabilities";
import { useQueryView } from "../../lib/loading/useQueryView";
import { ADMIN_BASE_PATH } from "../../lib/nav";
import { effectiveBrandId } from "../../lib/util";
import { ADMIN_TOAST_OPTS, useToast } from "../../lib/useToast";

export const MANTENCIONES_PATH = `${ADMIN_BASE_PATH}/mantenciones`;

type OpenDrawer =
  | { kind: "nueva" }
  | { kind: "asignar"; item: MaintenanceItemAdminSummary }
  | { kind: "editar"; item: MaintenanceItemAdminSummary };

export default function Mantenciones() {
  const session = useSession();
  const role: Role | undefined = session?.organization.role;
  const recipeQuery = useRecipe();
  const recipe = recipeQuery.data;
  // Quién administra el catálogo lo dice la receta (memberRoles[].orgAdmin)
  // más el administrador de plataforma, nunca el slug. Con la receta aún
  // cargando la query espera (waitingOn), no se apaga.
  const canManage = canManageMaintenanceItems(recipe, role, {
    platformAdmin: session?.user.platformAdmin === true,
  });
  const words = mantencionWords(recipe);
  const module = adminWords(recipe).module;
  const toast = useToast();

  const list = useMaintenanceItemsAdminList(canManage);
  const org = useMyOrganization();
  // Candidatas del picker y conteo de «creada en N tiendas»: las tiendas
  // del dashboard de facility, con marca efectiva (propia o de su zona),
  // como ChecklistTemplateAssignDrawer. Apagado si la receta no lo tiene.
  const facilityEnabled = isFacilityDashboardEnabled(recipe);
  const dashboard = useFacilityDashboard(facilityEnabled);
  const dashboardView = useQueryView(dashboard);

  const [open, setOpen] = useState<OpenDrawer | null>(null);

  const brands = useMemo(() => org.data?.brands ?? [], [org.data]);
  const brandNameById = useMemo(() => {
    const map = new Map<string, string>(brands.map((b) => [b.id, b.name]));
    // Lo que el dashboard ya sabe, por si GET /organizations/me no trae
    // marcas (rol de alcance menor, core-api anterior).
    for (const a of dashboard.data?.byAsset ?? []) {
      if (a.brandId && a.brandName && !map.has(a.brandId)) map.set(a.brandId, a.brandName);
    }
    for (const g of dashboard.data?.byAssetGroup ?? []) {
      if (g.brandId && g.brandName && !map.has(g.brandId)) map.set(g.brandId, g.brandName);
    }
    return map;
  }, [brands, dashboard.data]);

  const groups = useMemo(() => dashboard.data?.byAssetGroup ?? [], [dashboard.data]);
  const groupNameById = useMemo(() => new Map(groups.map((g) => [g.id, g.name])), [groups]);
  const storesWithBrand = useMemo(
    () =>
      (dashboard.data?.byAsset ?? []).map((a) => ({
        item: {
          id: a.id,
          unit: a.unit,
          address: a.address,
          assetGroupName: a.assetGroupId ? (groupNameById.get(a.assetGroupId) ?? null) : null,
        } satisfies StorePickerItem,
        brandId: effectiveBrandId(a, groups),
      })),
    [dashboard.data, groups, groupNameById]
  );

  function storesFor(brandId: string | null): StorePickerItem[] {
    return storesWithBrand
      .filter((s) => brandId === null || s.brandId === brandId)
      .map((s) => s.item);
  }
  function storeCountFor(brandId: string | null): number | null {
    if (!dashboard.data) return null;
    return storesFor(brandId).length;
  }

  function saved(message: string) {
    toast.show(message, "ok", ADMIN_TOAST_OPTS);
  }

  const storesLoading = facilityEnabled && dashboardView.kind === "loading";

  // Receta cargada y sin permiso: App.tsx ya redirige; aquí no hay nada
  // que mostrar y la query quedó apagada.
  if (recipe !== undefined && !canManage) return null;

  const headers = ["Mantención", "Frecuencia", "Aplica a", ""];

  return (
    <div className="page">
      <PageHeader
        eyebrow={module}
        title="Mantenciones periódicas"
        sub={`Define qué se mantiene, cada cuánto y en qué ${words.assetPluralLower}. Supervisores y jefes de ${words.assetLower} marcan cada una como realizada.`}
        actions={
          <button
            type="button"
            className="btn btn--primary"
            title="Crear una mantención periódica nueva"
            onClick={() => setOpen({ kind: "nueva" })}
          >
            <Icon.Plus s={14} />
            Nueva mantención
          </button>
        }
      />

      <QueryBoundary
        query={list}
        waitingOn={[recipeQuery]}
        label="las mantenciones"
        skeleton={
          <Panel flush>
            <SkelTable cols={4} headers={headers} />
          </Panel>
        }
        isEmpty={(data) => data.items.every((it) => !it.active)}
        empty={
          <Panel>
            <EmptyState
              title="Sin mantenciones"
              description={`Crea la primera con «Nueva mantención»: qué se mantiene, cada cuánto y en qué ${words.assetPluralLower}.`}
            />
          </Panel>
        }
      >
        {(data) => (
          <Panel flush>
            <table className="dtable">
              <thead>
                <tr>
                  <th scope="col">Mantención</th>
                  <th scope="col">Frecuencia</th>
                  <th scope="col">Aplica a</th>
                  <th scope="col">
                    <span className="sr-only">Acciones</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.items
                  .filter((it) => it.active)
                  .map((it) => (
                    <tr key={it.id}>
                      <td className="cell-strong">{it.label}</td>
                      <td>
                        <span className="cell-meta">{frequencyLabel(it.cadenceDays)}</span>
                      </td>
                      <td>{appliesToLabel(it, words, brandNameById)}</td>
                      <td className="cell-actions">
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm"
                          title={`Elegir en qué ${words.assetPluralLower} aplica`}
                          onClick={() => setOpen({ kind: "asignar", item: it })}
                        >
                          Asignar {words.assetPluralLower}
                        </button>
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm"
                          title="Cambiar nombre o frecuencia"
                          onClick={() => setOpen({ kind: "editar", item: it })}
                        >
                          Editar
                        </button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </Panel>
        )}
      </QueryBoundary>

      {open?.kind === "nueva" && (
        <NuevaMantencionDrawer
          words={words}
          brands={brands}
          storeCountFor={storeCountFor}
          storesLoading={storesLoading}
          onClose={() => setOpen(null)}
          onSaved={saved}
        />
      )}
      {open?.kind === "asignar" && (
        <AsignarTiendasDrawer
          item={open.item}
          words={words}
          stores={storesFor(open.item.brandId)}
          storesLoading={storesLoading}
          storesSkelVisible={dashboardView.kind === "loading" && dashboardView.visible}
          hasBrand={open.item.brandId !== null}
          brandName={open.item.brandId ? (brandNameById.get(open.item.brandId) ?? null) : null}
          onClose={() => setOpen(null)}
          onSaved={saved}
        />
      )}
      {open?.kind === "editar" && (
        <EditarMantencionDrawer
          item={open.item}
          words={words}
          onClose={() => setOpen(null)}
          onSaved={saved}
        />
      )}
    </div>
  );
}
